// hooks/usePhoneCountry.js
//
// The country code beside a phone number field: the visitor's own country,
// from their time zone (instant, and not moved by a VPN), else from where
// Cloudflare sees them (services/visitorCountry.js). When neither says, it is
// null and the picker starts blank - the visitor is asked to pick
// (hooks/useCountryNudge) rather than given a guess, because a wrong code
// sends the SMS nowhere.
//
// Detection never overrides a choice: not one the person made in the picker
// while it was still running, and not `initial` - a country already known,
// such as the one the RSVP popup hands to /login.

import { useCallback, useEffect, useRef, useState } from 'react';
import { phoneCountryFor, phoneCountryForTimeZone } from '../shared/authOptions';
import { networkCountry } from '../services/visitorCountry';

/** The PHONE_COUNTRIES entry for this browser's time zone, or null. */
function timeZoneCountry() {
  try {
    return phoneCountryForTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

/**
 * @param {string} [initial]  a country ('IN') or dial code ('+91') already known
 * @returns {[object|null, (country: object) => void]}  a PHONE_COUNTRIES entry (or null) and its setter
 */
export default function usePhoneCountry(initial) {
  const known = phoneCountryFor(initial);
  const [country, setCountryState] = useState(() => known || timeZoneCountry());
  const chosen = useRef(!!known);

  useEffect(() => {
    if (chosen.current || timeZoneCountry()) return undefined;
    let cancelled = false;
    networkCountry().then((iso2) => {
      const match = phoneCountryFor(iso2);
      if (!cancelled && !chosen.current && match) setCountryState(match);
    });
    return () => { cancelled = true; };
  }, []);

  const setCountry = useCallback((next) => {
    chosen.current = true;
    setCountryState(next);
  }, []);

  return [country, setCountry];
}
